/**
 * CopyButton — one icon button for "give me this whole block of text".
 *
 * Founder request 2026-07-21: a copy affordance on every block a human
 * would otherwise drag-select (voice transcripts, sent prompts, agent
 * responses, notes, synthesized answers, judge transcripts, live output).
 *
 * Design decisions, all inherited from neighbours rather than invented:
 *   - Glyph is lucide `Copy` (two overlapping squares), NOT `Clipboard` —
 *     the clipboard-with-clasp glyph is already spoken for by the secrets
 *     UI (settings/credential-detail-v2.tsx) and reads as a different
 *     affordance.
 *   - 28×28 visual box with `h-3.5 w-3.5` glyph, matching the delete
 *     button it sits beside in the Voice tab. On coarse pointers an
 *     invisible inset ::after grows the tap area to ≥44px WITHOUT
 *     changing the visual size — the same trick as layout/scope-bar.tsx.
 *     (An unconditional 44px min-height is also what
 *     tests/unit/touch-target-responsiveness.test.ts rejects.)
 *   - No `focus-visible:` classes: styles/global.css already paints a
 *     2px ring on `:focus-visible` for everything, and the sibling delete
 *     button relies on exactly that.
 *
 * Truthfulness contract, in two halves:
 *   - The checkmark appears ONLY after an awaited, genuinely successful
 *     write. A refused clipboard renders an error glyph plus a toast
 *     telling the user to copy by hand — it never lies.
 *   - "Nothing to copy" is NOT a failure, and must never be reported as
 *     one. `writeToClipboard` returns `false` for both an empty string and
 *     a refused clipboard, so a rendered button with no text would paint
 *     the red error state and toast "Copy the text manually" about text
 *     that does not exist. That is fixed HERE, once, rather than by asking
 *     seven call sites to remember a `{text && …}` guard: a CopyButton with
 *     no text renders nothing at all.
 */
import { Check, CircleAlert, Copy } from 'lucide-react';
import * as React from 'react';

import { useCopyToClipboard } from '../../lib/use-copy-to-clipboard';
import { cn } from '../../lib/utils';
import { useToasterOptional } from './toast';

/**
 * The invisible ::after that turns a small icon button into a finger-sized
 * tap target on coarse pointers only.
 *
 * Exported because a copy button is never alone: it lands in clusters of
 * 20-28px icon buttons, and a positioned element hit-tests ABOVE its
 * unpositioned in-flow siblings. If only the copy button carried this, its
 * expanded box would sit on top of the neighbour's left edge and steal the
 * tap. Every button in a cluster gets it, and the cluster's gap is widened
 * on coarse pointers so that (2 × inset) ≤ gap and the expanded boxes meet
 * without overlapping. `-inset-x-*` / `-inset-y-*` are split so a horizontal
 * cluster can trade width for height where a 44px-wide box will not fit.
 */
export const COARSE_TAP_EXPANSION =
  "relative pointer-coarse:after:absolute pointer-coarse:after:-inset-x-2 pointer-coarse:after:-inset-y-2 pointer-coarse:after:content-['']";

export interface CopyButtonProps {
  /**
   * The exact string to place on the clipboard. Must already be in memory.
   * Empty / absent ⇒ the component renders nothing.
   */
  text: string | null | undefined;
  /** Idle aria-label / tooltip, e.g. "Copy transcript". */
  label?: string;
  /** Overrides the 28×28 box (tailwind-merge wins on conflicts). */
  className?: string;
  /** Overrides the 14×14 glyph. */
  iconClassName?: string;
  /**
   * Render the current state label as VISIBLE text beside the glyph, turning
   * the icon button into a labelled pill. Used where the copy affordance sits
   * in a toolbar of labelled buttons (the Brain source reader) and a bare
   * glyph would read as a different weight of control.
   *
   * The visible text is the same string as `aria-label`, which is what keeps
   * WCAG 2.5.3 (label in name) satisfied without a second prop.
   */
  showLabel?: boolean;
  'data-testid'?: string;
}

export function CopyButton({
  text,
  label = 'Copy',
  className,
  iconClassName,
  showLabel = false,
  'data-testid': testId,
}: CopyButtonProps): React.JSX.Element | null {
  const { state, copy } = useCopyToClipboard();
  // Optional on purpose: this primitive is rendered inside <details>
  // summaries and message rows that unit tests mount without the app
  // shell. A missing provider must not throw — it just means no toast.
  const toaster = useToasterOptional();

  const onClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      // The button lives inside clickable parents — a <summary> that
      // toggles a <details>, and message/note rows with their own
      // handlers. A copy must never also open, toggle or navigate.
      // stopPropagation covers React handlers on ancestors; preventDefault
      // kills the <details> toggle, which is a browser default action.
      // (It cannot stop a raw addEventListener on an ancestor: React 17+
      // delegates at the root, so the native event has already bubbled by
      // the time this runs. No copy site here uses one.)
      e.stopPropagation();
      e.preventDefault();
      void copy(text ?? '').then((ok) => {
        if (ok) return;
        toaster?.toast({
          variant: 'warning',
          title: 'Copy failed',
          description: 'Copy the text manually.',
        });
      });
    },
    [copy, text, toaster],
  );

  // AFTER the hooks (rules-of-hooks) and BEFORE any markup: no text means no
  // affordance. See the truthfulness contract above.
  if (!text) return null;

  const stateLabel =
    state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed' : label;

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        aria-label={stateLabel}
        title={stateLabel}
        data-testid={testId}
        data-copy-state={state}
        // ORDER IS LOAD-BEARING, and it was wrong until 2026-07-21.
        //
        // `cn` is tailwind-merge, so within one conflict group (here: text
        // colour, hover background, hover text colour) the LAST class wins.
        // With `className` emitted last, a per-site tint swallowed the outcome
        // colours. Measured by mounting the real component and forcing a
        // failed write:
        //
        //   className (none)                  -> failed reads text-error   ✅
        //   notes-tab   (…text-muted-foreground…) -> stayed muted grey     ❌
        //   debate-tab  (…text-success/80…)       -> painted GREEN         ❌
        //
        // Green for a copy that did NOT happen is the exact class of lie this
        // whole feature exists to stop. So: idle colours come BEFORE
        // `className` (a site may retint the resting button — the Debate
        // consensus chip is success-green, the note cluster speaks accent),
        // and the outcome colours come AFTER it (no site may retint an
        // outcome).
        className={cn(
          'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors',
          COARSE_TAP_EXPANSION,
          // Idle. NOT `hover:bg-muted`: half of these buttons sit on a
          // muted-tinted ground (the user prompt bubble is `bg-muted/60`, the
          // Ask answer card `bg-muted/20`), where muted-on-muted is a 1.6 L
          // delta in light and 2.4 in dark — i.e. no visible hover at all. A
          // translucent foreground wash is ~8-9 L against every ground in the
          // app, both themes.
          'text-muted-foreground hover:bg-foreground/10 hover:text-foreground',
          className,
          // Outcome — after `className` on purpose. See the note above.
          state === 'copied' && 'text-success hover:bg-success/10 hover:text-success',
          state === 'failed' && 'text-error hover:bg-error/10 hover:text-error',
        )}
      >
        {state === 'copied' ? (
          <Check className={cn('h-3.5 w-3.5', iconClassName)} aria-hidden />
        ) : state === 'failed' ? (
          <CircleAlert className={cn('h-3.5 w-3.5', iconClassName)} aria-hidden />
        ) : (
          <Copy className={cn('h-3.5 w-3.5', iconClassName)} aria-hidden />
        )}
        {showLabel && <span>{stateLabel}</span>}
      </button>
      {/* Announce the outcome to screen readers: a changing aria-label is not
          reliably re-read on an already-focused element, a live region is.
          It is a SIBLING of the button, never a child: ARIA defines
          role=button as Children Presentational, so a live region nested
          inside it is flattened into the accessible name and may never
          announce — which would leave a screen-reader user with no
          confirmation at all. `sr-only` is position:absolute, so this is not
          a flex item and adds no layout. */}
      <span className="sr-only" role="status" aria-live="polite">
        {state === 'idle' ? '' : stateLabel}
      </span>
    </>
  );
}
